import React, { useCallback } from 'react';
import { ContentType, LearningHeader } from '@atlassian/learning-components';
import { useLocation, useNavigate } from 'react-router-dom';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { getInspectModeData } from 'src/utils/contentful';
import { User } from 'src/types';
import { sendUIEvent } from 'src/utils/analytics';
import { FEEDBACK_PAGE_FF } from 'src/constants';
import { LearningProgressStatus, mapServerStatusToComponentsStatus } from 'src/services/http/progress';
import { getSlug } from 'src/utils/slug';
import { getCourseUrl, getPathUrl, withPrefix } from 'src/utils/getUrl';
import { useCourseProgress } from 'src/store/progress/progressStore';
import { navigateToNextItem, getNextItemSlug } from 'src/pages/utils/navigation';

export interface OldLearningPathHeaderProps {
  learningPath: ContentfulLearningPath;
  user: User | null | undefined;
  onLoginUser: () => void;
  onSave: () => void;
  isLearningPathSaved: boolean;
  isLoading: boolean;
}

const FIELD_ID = 'courseId';

export const OldLearningPathHeader: React.FC<OldLearningPathHeaderProps> = ({
  learningPath,
  onLoginUser,
  user,
  onSave,
  isLearningPathSaved,
  isLoading,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [{ progressForCourses, isLoadingProgressForCourses }] = useCourseProgress();

  const isFeedbackPageAccessible = FeatureGates.checkGate(FEEDBACK_PAGE_FF);

  const learningPathSlug = getSlug(learningPath);

  const handleSignIn = useCallback(() => {
    if (onLoginUser) {
      sendUIEvent('learningPathScreen', 'button', 'clicked', 'signInButton', { objectType: 'learningPathHeader' });
      onLoginUser();
    }
  }, [onLoginUser]);

  const navigateToCourse = useCallback(
    (courseSlug: string) => navigate(withPrefix(getCourseUrl(courseSlug), location.pathname.replace(/\/$/, ''))),
    [navigate, location],
  );

  const startLearningPath = useCallback(() => {
    const courses = learningPath?.coursesCollection?.items;

    if (!courses?.length) {
      return;
    }

    navigateToNextItem({
      items: courses,
      progress: {
        overallStatus: progressForCourses?.overallStatus ?? LearningProgressStatus.NOT_STARTED,
        courses: progressForCourses?.courses || [],
      },
      itemIdField: FIELD_ID,
      contentSlug: learningPathSlug,
      contentType: ContentType.LEARNING_PATH,
      userId: user?.account_id,
      navigateToItem: navigateToCourse,
    });
  }, [learningPath, progressForCourses, navigateToCourse, user, learningPathSlug]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave();
      sendUIEvent('learningPathScreen', 'button', 'clicked', 'saveForLaterButton', {
        resourceSlug: learningPathSlug,
        resourceType: 'learningPath',
      });
    }
  }, [onSave, learningPathSlug]);

  const shouldShowFeedback =
    progressForCourses?.overallStatus === LearningProgressStatus.COMPLETED && isFeedbackPageAccessible;

  const navigateToFeedbackPage = useCallback(() => {
    const url = getPathUrl(learningPathSlug.replace(/\/$/, ''), true);

    window.open(url, '_blank');
  }, [learningPathSlug]);

  const handleHrefLink = useCallback((): string => {
    const courses = learningPath?.coursesCollection?.items;
    const locationPrefix = location.pathname.replace(/\/$/, '');

    if (!courses?.length) {
      return '';
    }

    const nextCourseSlug = getNextItemSlug({
      items: courses,
      progress: {
        overallStatus: progressForCourses?.overallStatus ?? LearningProgressStatus.NOT_STARTED,
        courses: progressForCourses?.courses || [],
      },
      itemIdField: FIELD_ID,
      contentType: ContentType.LEARNING_PATH,
    });

    return withPrefix(`${locationPrefix}${getCourseUrl(nextCourseSlug)}`);
  }, [learningPath, location.pathname, progressForCourses]);

  return (
    <div data-testid="learning-path-header">
      <LearningHeader
        inspectMode={getInspectModeData(learningPath || { sys: { id: '' } }, ['title'])}
        anonymousView={!user?.account_id}
        heading={learningPath?.title || 'Learning Path Title Not Found'}
        urlToShare={globalThis?.location?.href}
        onSignIn={handleSignIn}
        onStartCourse={startLearningPath}
        progressStatus={mapServerStatusToComponentsStatus(progressForCourses?.overallStatus)}
        isLoading={isLoading || isLoadingProgressForCourses}
        tagLine=""
        contentType={ContentType.LEARNING_PATH}
        onSave={handleSave}
        isSaved={isLearningPathSaved}
        shouldShowFeedback={shouldShowFeedback}
        onStartFeedback={navigateToFeedbackPage}
        href={handleHrefLink()}
      />
    </div>
  );
};
